import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Target, Plus, Edit, Trash2, MapPin, Building2, Upload, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useTenant } from '@/contexts/TenantContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CompetitorSitesBulkImport } from '@/components/charging/CompetitorSitesBulkImport';
interface CompetitorOrganisation {
  id: string;
  name: string;
  accent_color: string;
  logo_url: string | null;
  website: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}
interface CompetitorSite {
  id: string;
  name: string;
  organisation_id: string;
  address: string | null;
  city: string;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  chargers_count: number | null;
  power_kw: number | null;
  created_at: string;
  updated_at: string;
}

// Helper om competitor favicon op te halen
const getCompetitorLogo = (org: CompetitorOrganisation): string => {
  // Als er een geüploade logo is, gebruik die
  if (org.logo_url) {
    return org.logo_url;
  }
  
  // Als er een website is, haal favicon op via Google's service
  if (org.website) {
    // Clean up website URL (remove https://, http://, www.)
    const cleanDomain = org.website
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]; // Get only domain
    
    return `https://www.google.com/s2/favicons?sz=64&domain=${cleanDomain}`;
  }
  
  // Final fallback: Greenvision favicon
  return '/favicon.png';
};

export default function ChargingConcurrentie() {
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const { MapComponent } = useMapWithProvider();
  
  // Determine portal context
  const inCRM = location.pathname.startsWith('/crm');
  const portalType = inCRM ? 'crm' : 'charging';
  
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<CompetitorOrganisation | null>(null);
  const [selectedSite, setSelectedSite] = useState<CompetitorSite | null>(null);
  const [siteOrgId, setSiteOrgId] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    accent_color: '#e53935',
    website: ''
  });
  const [siteFormData, setSiteFormData] = useState({
    name: '',
    address: '',
    city: '',
    notes: '',
    chargers_count: '',
    power_kw: ''
  });

  // Fetch organisations
  const {
    data: organisations = [],
    isLoading: isLoadingOrgs
  } = useQuery({
    queryKey: ['competitor-organisations'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('competitor_organisations').select('*').order('name');
      if (error) throw error;
      return data as CompetitorOrganisation[];
    }
  });

  // Fetch sites
  const {
    data: sites = [],
    isLoading: isLoadingSites
  } = useQuery({
    queryKey: ['competitor-sites'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('competitor_sites').select('*').order('name');
      if (error) throw error;
      return data as CompetitorSite[];
    }
  });

  // Save organisation mutation
  const saveOrgMutation = useMutation({
    mutationFn: async (orgData: typeof orgFormData) => {
      let logoUrl = selectedOrg?.logo_url || null;

      // Upload logo if file selected
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const {
          error: uploadError
        } = await supabase.storage.from('competitor-logos').upload(fileName, logoFile);
        if (uploadError) throw uploadError;
        const {
          data: {
            publicUrl
          }
        } = supabase.storage.from('competitor-logos').getPublicUrl(fileName);
        logoUrl = publicUrl;
      }
      const fullData = {
        ...orgData,
        logo_url: logoUrl,
        tenant_id: currentTenant?.id,
        created_by: user?.id
      };
      if (selectedOrg) {
        const {
          error
        } = await supabase.from('competitor_organisations').update(fullData).eq('id', selectedOrg.id);
        if (error) throw error;
      } else {
        const {
          error
        } = await supabase.from('competitor_organisations').insert(fullData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitor-organisations']
      });
      toast({
        title: selectedOrg ? 'Bedrijf bijgewerkt' : 'Bedrijf toegevoegd'
      });
      closeOrgModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Fout bij opslaan',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Scrape competitor locations mutation
  const scrapeLocationsMutation = useMutation({
    mutationFn: async ({ orgId, orgName }: { orgId: string; orgName: string }) => {
      console.log('[SCRAPE] Starting scrape for:', orgName);
      
      const { data, error } = await supabase.functions.invoke('scrape-competitor-locations', {
        body: {
          organisation_id: orgId,
          competitor_name: orgName,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['competitor-sites'] });
      
      let description = `${data.added} nieuwe locaties toegevoegd`;
      if (data.updated > 0) {
        description += `, ${data.updated} bijgewerkt`;
      }
      if (data.skipped > 0) {
        description += `, ${data.skipped} overgeslagen (duplicaten)`;
      }
      
      toast({
        title: 'Locaties gescraped',
        description,
      });
      
      if (data.errors && data.errors.length > 0) {
        console.warn('[SCRAPE] Errors:', data.errors);
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij scrapen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Import LEAP24 locations mutation
  const importLeap24Mutation = useMutation({
    mutationFn: async () => {
      const LEAP24_ORG_ID = 'c4fe4e22-8a79-47ef-ac23-c2fc4a63afc4';
      
      // Delete old LEAP24 sites
      const { error: deleteError } = await supabase
        .from('competitor_sites')
        .delete()
        .eq('organisation_id', LEAP24_ORG_ID);
      
      if (deleteError) throw deleteError;

      // Insert 20 new LEAP24 locations
      const newSites = [
        { name: 'LEAP24 Almere', city: 'Almere', address: 'Transistorstraat 54', chargers_count: 4, power_kw: 160, notes: '4 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Badhoevedorp', city: 'Badhoevedorp', address: 'Schipholweg 285', chargers_count: 5, power_kw: 300, notes: '5 x DC 300kW (10 opstelplaatsen). Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Eindhoven', city: 'Eindhoven', address: 'Wekkerstraat 20', chargers_count: 2, power_kw: 120, notes: '2 x DC 120kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Enschede', city: 'Enschede', address: 'Het Poolman 13', chargers_count: 5, power_kw: 160, notes: '5 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Geleen', city: 'Geleen', address: 'Industrieweg 1', chargers_count: 9, power_kw: 120, notes: '6 x DC 120kW, 3 x AC. Geschikt voor: Vrachtwagen, Bestelbus, Auto. Extra: Greenwheels deelauto' },
        { name: 'LEAP24 Groningen', city: 'Groningen', address: 'Lamsoor 7', chargers_count: 6, power_kw: 160, notes: '6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Haarlem', city: 'Haarlem', address: 'Kuipersweg 1', chargers_count: 7, power_kw: 300, notes: '1 x DC 300kW, 6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Hoogeveen', city: 'Hoogeveen', address: 'Meester Cramerweg 7', chargers_count: 5, power_kw: 300, notes: '1 x DC 300kW, 4 x DC 120kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Leeuwarden', city: 'Leeuwarden', address: 'Ampèrestraat 14k', chargers_count: 18, power_kw: 160, notes: '12 x DC 160kW, 6 x AC. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Maastricht', city: 'Maastricht', address: 'Karveelweg 25', chargers_count: 1, power_kw: 120, notes: '1 x DC 120kW (2 laadpunten). Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Nijmegen', city: 'Nijmegen', address: 'Oude Groenewoudseweg 365', chargers_count: 13, power_kw: 160, notes: '8 x DC 160kW, 5 x AC 22kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto. Extra: Greenwheels deelauto' },
        { name: 'LEAP24 Roermond', city: 'Roermond', address: 'Keulsebaan 509', chargers_count: 5, power_kw: 240, notes: '5 x DC 240kW (10 opstelplaatsen). Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Schagen', city: 'Schagen', address: 'De Lus 9H', chargers_count: 6, power_kw: 160, notes: '6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Sittard', city: 'Sittard', address: 'Kopenhagenstraat 40', chargers_count: 4, power_kw: 240, notes: '4 x DC 240kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Stadskanaal', city: 'Stadskanaal', address: 'Touwslager 3', chargers_count: 6, power_kw: 160, notes: '6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Steenwijk', city: 'Steenwijk', address: 'Eesergaard', chargers_count: 5, power_kw: 300, notes: '1 x DC 300kW, 4 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Vlissingen', city: 'Vlissingen', address: 'Energieweg 1', chargers_count: 6, power_kw: 160, notes: '6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Winschoten', city: 'Winschoten', address: 'Hellingbaan 1', chargers_count: 4, power_kw: 240, notes: '4 x DC 240kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Zuidbroek', city: 'Zuidbroek', address: 'Weg der Verenigde Naties 2', chargers_count: 3, power_kw: 240, notes: '3 x DC 240kW (6 plaatsen). Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
        { name: 'LEAP24 Zwolle', city: 'Zwolle', address: 'Graham Bellstraat 43', chargers_count: 6, power_kw: 160, notes: '6 x DC 160kW. Geschikt voor: Vrachtwagen, Bestelbus, Auto' },
      ];

      // Geocode and insert each site
      const sitesWithCoords = await Promise.all(
        newSites.map(async (site) => {
          try {
            const query = `${site.address}, ${site.city}`;
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&countrycodes=nl&limit=1`,
              { headers: { 'User-Agent': 'Greenvision Charging Portal' } }
            );
            const geoData = await response.json();
            
            if (geoData && geoData.length > 0) {
              return {
                ...site,
                organisation_id: LEAP24_ORG_ID,
                latitude: parseFloat(geoData[0].lat),
                longitude: parseFloat(geoData[0].lon),
              };
            }
            
            return { ...site, organisation_id: LEAP24_ORG_ID, latitude: null, longitude: null };
          } catch (error) {
            console.error(`Geocoding failed for ${site.city}:`, error);
            return { ...site, organisation_id: LEAP24_ORG_ID, latitude: null, longitude: null };
          }
        })
      );

      const { error: insertError } = await supabase
        .from('competitor_sites')
        .insert(sitesWithCoords);
      
      if (insertError) throw insertError;

      return { added: sitesWithCoords.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['competitor-sites'] });
      toast({
        title: 'LEAP24 geïmporteerd',
        description: `${data.added} nieuwe LEAP24 locaties toegevoegd`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij importeren',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Save site mutation
  const saveSiteMutation = useMutation({
    mutationFn: async (siteData: any) => {
      // Auto-geocode
      let latitude = siteData.latitude;
      let longitude = siteData.longitude;
      if ((!latitude || !longitude) && siteData.city) {
        const query = [siteData.address, siteData.city].filter(Boolean).join(', ');
        const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&countrycodes=nl&limit=1`, {
          headers: {
            'User-Agent': 'Greenvision Charging Portal'
          }
        });
        const geoData = await response.json();
        if (geoData && geoData.length > 0) {
          latitude = parseFloat(geoData[0].lat);
          longitude = parseFloat(geoData[0].lon);
        }
      }
      const fullData = {
        ...siteData,
        latitude,
        longitude,
        organisation_id: siteOrgId,
        chargers_count: siteData.chargers_count ? parseInt(siteData.chargers_count) : null,
        power_kw: siteData.power_kw ? parseInt(siteData.power_kw) : null
      };
      if (selectedSite) {
        const {
          error
        } = await supabase.from('competitor_sites').update(fullData).eq('id', selectedSite.id);
        if (error) throw error;
      } else {
        const {
          error
        } = await supabase.from('competitor_sites').insert(fullData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitor-sites']
      });
      toast({
        title: selectedSite ? 'Laadplein bijgewerkt' : 'Laadplein toegevoegd'
      });
      closeSiteModal();
    },
    onError: (error: any) => {
      console.error('Site save error:', error);
      let description = error.message;
      
      if (error.message?.includes('row-level security')) {
        description = 'Je hebt geen rechten om dit laadplein toe te voegen. Controleer of je aan een geldig bedrijf koppelt.';
      } else if (error.message?.includes('organisation_id')) {
        description = 'Selecteer eerst een bedrijf voordat je een laadplein toevoegt.';
      } else if (error.code === '23503') {
        description = 'Het geselecteerde bedrijf bestaat niet meer.';
      }
      
      toast({
        title: 'Fout bij opslaan laadplein',
        description,
        variant: 'destructive'
      });
    }
  });

  // Delete organisation mutation
  const deleteOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const {
        error
      } = await supabase.from('competitor_organisations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitor-organisations']
      });
      queryClient.invalidateQueries({
        queryKey: ['competitor-sites']
      });
      toast({
        title: 'Bedrijf verwijderd'
      });
    }
  });

  // Delete site mutation
  const deleteSiteMutation = useMutation({
    mutationFn: async (id: string) => {
      const {
        error
      } = await supabase.from('competitor_sites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitor-sites']
      });
      toast({
        title: 'Laadplein verwijderd'
      });
    }
  });

  // Auto-import LEAP24 if old data detected
  useEffect(() => {
    const LEAP24_ORG_ID = 'c4fe4e22-8a79-47ef-ac23-c2fc4a63afc4';
    const leap24Sites = sites.filter(s => s.organisation_id === LEAP24_ORG_ID);
    
    // If there are LEAP24 sites but fewer than 15, it's old data - trigger import
    if (leap24Sites.length > 0 && leap24Sites.length < 15 && !importLeap24Mutation.isPending) {
      console.log('Oude LEAP24 data gedetecteerd, automatisch importeren van nieuwe locaties...');
      importLeap24Mutation.mutate();
    }
  }, [sites, importLeap24Mutation]);

  // Group sites by organisation
  const sitesByOrg = useMemo(() => {
    const grouped: Record<string, CompetitorSite[]> = {};
    sites.forEach(site => {
      if (!grouped[site.organisation_id]) {
        grouped[site.organisation_id] = [];
      }
      grouped[site.organisation_id].push(site);
    });
    return grouped;
  }, [sites]);

  // Map sites with filtering
  const filteredMapSites = useMemo(() => {
    const sitesWithCoords = sites.filter(s => s.latitude && s.longitude);
    const filtered = selectedOrgId ? sitesWithCoords.filter(s => s.organisation_id === selectedOrgId) : sitesWithCoords;
    return filtered.map(site => {
      const org = organisations.find(o => o.id === site.organisation_id);
      return {
        id: site.id,
        name: site.name,
        address: site.address || site.city,
        latitude: site.latitude!,
        longitude: site.longitude!,
        type: 'competitor',
        color: org?.accent_color || '#e53935',
        organisation_id: site.organisation_id
      };
    });
  }, [sites, organisations, selectedOrgId]);
  const getMarkerColor = (site: any) => {
    return site.color || '#e53935';
  };
  const closeOrgModal = () => {
    setIsOrgModalOpen(false);
    setSelectedOrg(null);
    setOrgFormData({
      name: '',
      accent_color: '#e53935',
      website: ''
    });
    setLogoFile(null);
    setLogoPreview(null);
  };
  const closeSiteModal = () => {
    setIsSiteModalOpen(false);
    setSelectedSite(null);
    setSiteOrgId(null);
    setSiteFormData({
      name: '',
      address: '',
      city: '',
      notes: '',
      chargers_count: '',
      power_kw: ''
    });
  };
  const handleEditOrg = (org: CompetitorOrganisation) => {
    setSelectedOrg(org);
    setOrgFormData({
      name: org.name,
      accent_color: org.accent_color,
      website: org.website || ''
    });
    setLogoPreview(org.logo_url);
    setIsOrgModalOpen(true);
  };
  const handleEditSite = (site: CompetitorSite) => {
    setSelectedSite(site);
    setSiteOrgId(site.organisation_id);
    setSiteFormData({
      name: site.name,
      address: site.address || '',
      city: site.city,
      notes: site.notes || '',
      chargers_count: site.chargers_count?.toString() || '',
      power_kw: site.power_kw?.toString() || ''
    });
    setIsSiteModalOpen(true);
  };
  const handleAddSite = (orgId: string) => {
    setSiteOrgId(orgId);
    setIsSiteModalOpen(true);
  };
  const handleSaveOrg = () => {
    if (!orgFormData.name) {
      toast({
        title: 'Naam is verplicht',
        variant: 'destructive'
      });
      return;
    }
    saveOrgMutation.mutate(orgFormData);
  };
  const handleSaveSite = () => {
    if (!siteFormData.name || !siteFormData.city) {
      toast({
        title: 'Naam en plaats zijn verplicht',
        variant: 'destructive'
      });
      return;
    }
    
    if (!siteOrgId) {
      toast({
        title: 'Selecteer een bedrijf',
        description: 'Elk laadplein moet aan een bedrijf gekoppeld zijn',
        variant: 'destructive'
      });
      return;
    }
    
    saveSiteMutation.mutate(siteFormData);
  };
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const openOrgModal = (org: CompetitorOrganisation | null = null) => {
    if (org) {
      handleEditOrg(org);
    } else {
      setIsOrgModalOpen(true);
    }
  };
  const openSiteModal = (site: CompetitorSite | null = null) => {
    if (site) {
      handleEditSite(site);
    } else {
      setIsSiteModalOpen(true);
    }
  };
  return <div className="container mx-auto p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Target className="w-8 h-8" />
              Concurrentie
            </h1>
            <p className="text-muted-foreground mt-1">
              Beheer concurrerende bedrijven en hun laadpleinen
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => {
                if (confirm('Weet je zeker dat je alle oude LEAP24 locaties wilt verwijderen en de 20 nieuwe locaties wilt importeren?')) {
                  importLeap24Mutation.mutate();
                }
              }}
              disabled={importLeap24Mutation.isPending}
              variant="secondary"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importLeap24Mutation.isPending ? 'Importeren...' : 'Import LEAP24'}
            </Button>
            <Button onClick={() => {
            setSiteOrgId(null);
            openSiteModal(null);
          }} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Snel laadplein toevoegen
            </Button>
            <Button onClick={() => openOrgModal(null)}>
              <Building2 className="w-4 h-4 mr-2" />
              Nieuw bedrijf
            </Button>
          </div>
        </div>

        {/* Bulk Import Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Excel Bulk Import
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CompetitorSitesBulkImport
              organisations={organisations}
              onImportComplete={() => {
                queryClient.invalidateQueries({ queryKey: ['competitor-sites'] });
              }}
            />
          </CardContent>
        </Card>

        

        {/* Map */}
        <Card className="p-4">
          <div className="w-full h-[500px] rounded-lg overflow-hidden">
            {filteredMapSites.length === 0 ? <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Geen concurrenten op kaart</p>
                </div>
              </div> : <MapComponent sites={filteredMapSites} getMarkerColor={getMarkerColor} portalType={portalType} />}
          </div>
        </Card>

        {/* Logo Filter Bar */}
        <Card className="p-4">
          <div className="flex gap-3 items-center overflow-x-auto">
            <Button variant={selectedOrgId === null ? "default" : "outline"} onClick={() => setSelectedOrgId(null)} className="flex-shrink-0" size="sm">
              Alles ({sites.length})
            </Button>
            {organisations.map(org => <Button key={org.id} variant={selectedOrgId === org.id ? "default" : "outline"} onClick={() => setSelectedOrgId(org.id)} className="flex flex-col gap-1 h-auto p-3 flex-shrink-0" size="sm">
                <img 
                  src={getCompetitorLogo(org)} 
                  className="w-12 h-12 rounded object-contain bg-white p-1" 
                  alt={org.name}
                  onError={(e) => {
                    // Fallback if Google favicon fails
                    e.currentTarget.src = '/favicon.png';
                  }}
                />
                <span className="text-xs font-medium">{org.name}</span>
                <Badge variant="secondary" className="text-xs">
                  {sitesByOrg[org.id]?.length || 0}
                </Badge>
              </Button>)}
          </div>
        </Card>

        {/* Organisations & Sites List */}
        <Card className="p-6">
          {isLoadingOrgs || isLoadingSites ? <div className="text-center py-8">Laden...</div> : organisations.length === 0 ? <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Geen bedrijven toegevoegd</p>
            </div> : <Accordion type="single" collapsible className="space-y-2">
              {organisations.map(org => <AccordionItem key={org.id} value={org.id} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 flex-1">
                      <img 
                        src={getCompetitorLogo(org)} 
                        className="w-10 h-10 rounded object-contain bg-white p-1" 
                        alt={org.name}
                        onError={(e) => {
                          e.currentTarget.src = '/favicon.png';
                        }}
                      />
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold">{org.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {sitesByOrg[org.id]?.length || 0} laadpleinen
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={e => {
                            e.stopPropagation();
                            scrapeLocationsMutation.mutate({ 
                              orgId: org.id, 
                              orgName: org.name 
                            });
                          }}
                          disabled={scrapeLocationsMutation.isPending}
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          {scrapeLocationsMutation.isPending ? 'Bezig...' : 'Scrape'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={e => {
                    e.stopPropagation();
                    handleEditOrg(org);
                  }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={e => {
                    e.stopPropagation();
                    handleAddSite(org.id);
                  }}>
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={e => {
                    e.stopPropagation();
                    if (confirm('Weet je zeker dat je dit bedrijf wilt verwijderen? Alle laadpleinen worden ook verwijderd.')) {
                      deleteOrgMutation.mutate(org.id);
                    }
                  }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-4">
                      {sitesByOrg[org.id]?.length > 0 ? sitesByOrg[org.id].map(site => <Card key={site.id} className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium">{site.name}</h4>
                                {site.address && <p className="text-sm text-muted-foreground">{site.address}</p>}
                                <p className="text-sm text-muted-foreground">{site.city}</p>
                                <div className="flex gap-2 mt-1">
                                  {site.chargers_count && <Badge variant="outline" className="text-xs">
                                      {site.chargers_count} laders
                                    </Badge>}
                                  {site.power_kw && <Badge variant="outline" className="text-xs">
                                      {site.power_kw} kW
                                    </Badge>}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditSite(site)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => {
                        if (confirm('Weet je zeker dat je dit laadplein wilt verwijderen?')) {
                          deleteSiteMutation.mutate(site.id);
                        }
                      }}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>) : <p className="text-sm text-muted-foreground text-center py-4">
                          Geen laadpleinen toegevoegd
                        </p>}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>}
        </Card>
      </div>

      {/* Organisation Modal */}
      <Dialog open={isOrgModalOpen} onOpenChange={setIsOrgModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedOrg ? 'Bewerk bedrijf' : 'Nieuw bedrijf'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Naam *</label>
              <Input value={orgFormData.name} onChange={e => setOrgFormData({
              ...orgFormData,
              name: e.target.value
            })} placeholder="Bedrijfsnaam..." />
            </div>
            <div>
              <label className="text-sm font-medium">Accentkleur *</label>
              <div className="flex gap-2">
                <Input type="color" value={orgFormData.accent_color} onChange={e => setOrgFormData({
                ...orgFormData,
                accent_color: e.target.value
              })} className="w-20 h-10" />
                <Input value={orgFormData.accent_color} onChange={e => setOrgFormData({
                ...orgFormData,
                accent_color: e.target.value
              })} placeholder="#e53935" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Website</label>
              <Input value={orgFormData.website} onChange={e => setOrgFormData({
              ...orgFormData,
              website: e.target.value
            })} placeholder="https://..." />
            </div>
            <div>
              <label className="text-sm font-medium">Logo</label>
              <div className="space-y-2">
                {logoPreview && <div className="w-24 h-24 border rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                  </div>}
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('logo-upload')?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    {logoPreview ? 'Wijzig logo' : 'Upload logo'}
                  </Button>
                  <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeOrgModal}>
                Annuleren
              </Button>
              <Button onClick={handleSaveOrg} disabled={saveOrgMutation.isPending}>
                {saveOrgMutation.isPending ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Site Modal */}
      <Dialog open={isSiteModalOpen} onOpenChange={setIsSiteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedSite ? 'Bewerk laadplein' : 'Nieuw laadplein'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Bedrijf *</label>
              <Select value={siteOrgId || ''} onValueChange={(value) => setSiteOrgId(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer bedrijf..." />
                </SelectTrigger>
                <SelectContent>
                  {organisations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      <div className="flex items-center gap-2">
                        <img 
                          src={getCompetitorLogo(org)} 
                          className="w-5 h-5 rounded object-contain bg-white p-0.5" 
                          alt={org.name}
                          onError={(e) => {
                            e.currentTarget.src = '/favicon.png';
                          }}
                        />
                        {org.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Naam *</label>
              <Input value={siteFormData.name} onChange={e => setSiteFormData({
              ...siteFormData,
              name: e.target.value
            })} placeholder="Laadplein naam..." />
            </div>
            <div>
              <label className="text-sm font-medium">Adres</label>
              <Input value={siteFormData.address} onChange={e => setSiteFormData({
              ...siteFormData,
              address: e.target.value
            })} placeholder="Straat + nummer..." />
            </div>
            <div>
              <label className="text-sm font-medium">Plaats *</label>
              <Input value={siteFormData.city} onChange={e => setSiteFormData({
              ...siteFormData,
              city: e.target.value
            })} placeholder="Plaatsnaam..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Aantal laders</label>
                <Input type="number" value={siteFormData.chargers_count} onChange={e => setSiteFormData({
                ...siteFormData,
                chargers_count: e.target.value
              })} placeholder="12" />
              </div>
              <div>
                <label className="text-sm font-medium">Vermogen (kW)</label>
                <Input type="number" value={siteFormData.power_kw} onChange={e => setSiteFormData({
                ...siteFormData,
                power_kw: e.target.value
              })} placeholder="350" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Notities</label>
              <Textarea value={siteFormData.notes} onChange={e => setSiteFormData({
              ...siteFormData,
              notes: e.target.value
            })} placeholder="Extra informatie..." rows={3} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeSiteModal}>
                Annuleren
              </Button>
              <Button onClick={handleSaveSite} disabled={saveSiteMutation.isPending}>
                {saveSiteMutation.isPending ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}